import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Row, Col, Typography, Table, Tag, Progress, Statistic, Card, Tooltip } from 'antd';
import {
  CustomerServiceOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { StatCard, DataCard, MonoText } from '../components/common';
import { useTenant } from '../context/TenantContext';
import {
  useAutotaskCompanies,
  useAutotaskTickets,
  useAutotaskStats,
  useAutotaskSLA,
  useAutotaskIdFromCipp,
} from '../hooks';

const { Title, Text } = Typography;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

// Table chrome comes from the root ConfigProvider (theme spec section 2.4): no page-local .ant-* overrides.
const StyledTable = styled(Table)``;

const SLACard = styled(Card)`
  text-align: center;
`;

const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'red';
    case 'high': return 'orange';
    case 'medium': return 'blue';
    case 'low': return 'green';
    default: return 'default';
  }
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'new': return 'blue';
    case 'in progress': return 'processing';
    case 'waiting': return 'orange';
    case 'complete': return 'green';
    case 'closed': return 'default';
    default: return 'default';
  }
};

const MappingWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.sm};
  background: var(--odin-status-awaiting-bg);
  border: 1px solid var(--odin-warning);
  border-radius: 4px;
  color: var(--odin-warning);
  font-size: 12px;
  margin-bottom: 16px;
`;

const ServiceDeskPage = () => {
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to Autotask company ID using customer mapping
  const {
    autotaskId: mappedAutotaskId,
    isResolved,
    isLoading: isMappingLoading,
    customer,
  } = useAutotaskIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId,
  });

  // Use mapped Autotask ID
  const companyId = mappedAutotaskId;
  const hasAutotaskMapping = isResolved;

  // Fetch data
  const { data: companies, isLoading: companiesLoading } = useAutotaskCompanies();
  const { data: ticketsData, isLoading: ticketsLoading, refetch: refetchTickets } = useAutotaskTickets(companyId, {
    enabled: companyId !== null,
  });
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useAutotaskStats(companyId, {
    enabled: companyId !== null,
  });
  const { data: slaData, isLoading: slaLoading, refetch: refetchSLA } = useAutotaskSLA(companyId, {
    enabled: companyId !== null,
  });

  // Parse tickets from Autotask-MCP response
  const tickets = useMemo(() => {
    if (!ticketsData) return [];
    if (typeof ticketsData === 'string') {
      try {
        const parsed = JSON.parse(ticketsData);
        return parsed.data?.tickets || parsed.tickets || parsed || [];
      } catch {
        return [];
      }
    }
    if (ticketsData.data?.tickets) return ticketsData.data.tickets;
    if (ticketsData.tickets) return ticketsData.tickets;
    if (Array.isArray(ticketsData)) return ticketsData;
    if (ticketsData.content?.[0]?.text) {
      try {
        const parsed = JSON.parse(ticketsData.content[0].text);
        return parsed.data?.tickets || parsed.tickets || parsed || [];
      } catch {
        return [];
      }
    }
    return [];
  }, [ticketsData]);

  // Parse stats from Autotask-MCP response
  const stats = useMemo(() => {
    if (!statsData) return null;
    if (typeof statsData === 'string') {
      try { return JSON.parse(statsData); } catch { return null; }
    }
    if (statsData.data) return statsData.data;
    if (statsData.content?.[0]?.text) {
      try { return JSON.parse(statsData.content[0].text); } catch { return null; }
    }
    return statsData;
  }, [statsData]);

  // Parse SLA data from Autotask-MCP response
  const sla = useMemo(() => {
    if (!slaData) return null;
    if (typeof slaData === 'string') {
      try { return JSON.parse(slaData); } catch { return null; }
    }
    if (slaData.data) return slaData.data;
    if (slaData.content?.[0]?.text) {
      try { return JSON.parse(slaData.content[0].text); } catch { return null; }
    }
    return slaData;
  }, [slaData]);

  // Calculate statistics
  const openTickets = tickets?.filter(t => !['Complete', 'Closed', 'complete', 'closed'].includes(t.status)).length || 0;
  const criticalTickets = tickets?.filter(t => t.priority?.toLowerCase() === 'critical').length || 0;
  const highTickets = tickets?.filter(t => t.priority?.toLowerCase() === 'high').length || 0;
  const slaCompliance = sla?.slaCompliance || sla?.compliance || sla?.overallCompliance || stats?.slaCompliance || null;
  const avgResponseTime = stats?.avgResponseTime || stats?.averageResponseTime || null;

  // Show mapping warning if no Autotask mapping found
  const showMappingWarning = cippTenantId && !hasAutotaskMapping && !isMappingLoading;

  // Combined loading state
  const isLoading = isMappingLoading || ticketsLoading || statsLoading || slaLoading;

  // Ticket table columns
  const ticketColumns = [
    {
      title: 'Ticket #',
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      width: 100,
      render: (ticketNumber) => <MonoText>{ticketNumber}</MonoText>,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority) => (
        <Tag color={getPriorityColor(priority)}>
          {priority || 'Normal'}
        </Tag>
      ),
      filters: [
        { text: 'Critical', value: 'critical' },
        { text: 'High', value: 'high' },
        { text: 'Medium', value: 'medium' },
        { text: 'Low', value: 'low' },
      ],
      onFilter: (value, record) => record.priority?.toLowerCase() === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status || 'Unknown'}
        </Tag>
      ),
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedResource',
      key: 'assigned',
      ellipsis: true,
      render: (resource) => resource || 'Unassigned',
    },
    {
      title: 'Created',
      dataIndex: 'createDate',
      key: 'created',
      width: 150,
      render: (date) => date ? new Date(date).toLocaleDateString() : '--',
      sorter: (a, b) => new Date(a.createDate || 0) - new Date(b.createDate || 0),
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivityDate',
      key: 'lastActivity',
      width: 150,
      render: (date) => date ? new Date(date).toLocaleDateString() : '--',
    },
  ];

  return (
    <DashboardLayout>
      <PageHeader>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <CustomerServiceOutlined style={{ marginRight: 12, color: 'var(--odin-accent)' }} />
          Autotask
        </Title>
        <Text style={{ color: 'var(--odin-text-secondary)' }}>
          {selectedTenant
            ? `${selectedTenant.displayName}${customer ? ` (Autotask: ${customer.displayName})` : ''} - Ticket Management`
            : 'All Companies - Autotask PSA'}
        </Text>
      </PageHeader>

      {/* Mapping Warning */}
      {showMappingWarning && (
        <MappingWarning>
          <LinkOutlined />
          <span>
            No Autotask company mapping found for this tenant. Add the autotask ID to the customer's platformIds in Odin-MCP configuration.
          </span>
        </MappingWarning>
      )}

      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={FileTextOutlined}
            value={hasAutotaskMapping ? openTickets : null}
            label="Open Tickets"
            loading={isLoading}
            tooltip={hasAutotaskMapping ? 'Open tickets in Autotask' : 'Configure Autotask mapping to view data'}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={ExclamationCircleOutlined}
            value={hasAutotaskMapping ? criticalTickets + highTickets : null}
            label="High Priority"
            color="var(--odin-error)"
            loading={isLoading}
            tooltip={hasAutotaskMapping ? 'Critical and high priority tickets' : 'Configure Autotask mapping to view data'}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SLACard>
            <Statistic
              title="Avg Response Time"
              value={avgResponseTime !== null ? avgResponseTime : '--'}
              suffix={avgResponseTime !== null ? 'min' : ''}
              prefix={<ClockCircleOutlined style={{ color: 'var(--odin-accent)' }} />}
              loading={isLoading}
            />
          </SLACard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <SLACard>
            <Statistic
              title="SLA Compliance"
              value={slaCompliance !== null ? slaCompliance : '--'}
              suffix={slaCompliance !== null ? '%' : ''}
              prefix={<CheckCircleOutlined style={{ color: slaCompliance >= 95 ? 'var(--odin-success)' : slaCompliance >= 80 ? 'var(--odin-warning)' : 'var(--odin-error)' }} />}
              loading={isLoading}
            />
          </SLACard>
        </Col>
      </Row>

      {/* Priority Breakdown */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <DataCard title="Ticket Priority Breakdown" icon={ExclamationCircleOutlined}>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Tag color="red" style={{ width: '100%', textAlign: 'center', padding: '8px' }}>
                  Critical: {tickets?.filter(t => t.priority?.toLowerCase() === 'critical').length || 0}
                </Tag>
              </Col>
              <Col xs={12} sm={6}>
                <Tag color="orange" style={{ width: '100%', textAlign: 'center', padding: '8px' }}>
                  High: {tickets?.filter(t => t.priority?.toLowerCase() === 'high').length || 0}
                </Tag>
              </Col>
              <Col xs={12} sm={6}>
                <Tag style={{ width: '100%', textAlign: 'center', padding: '8px', background: 'var(--odin-status-active-bg)', color: 'var(--odin-status-active-text)', borderColor: 'var(--odin-status-active-border)' }}>
                  Medium: {tickets?.filter(t => t.priority?.toLowerCase() === 'medium').length || 0}
                </Tag>
              </Col>
              <Col xs={12} sm={6}>
                <Tag color="green" style={{ width: '100%', textAlign: 'center', padding: '8px' }}>
                  Low: {tickets?.filter(t => t.priority?.toLowerCase() === 'low').length || 0}
                </Tag>
              </Col>
            </Row>
          </DataCard>
        </Col>
      </Row>

      {/* Tickets Table */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <DataCard
            title="Open Tickets"
            icon={FileTextOutlined}
            loading={isLoading}
            empty={!tickets?.length}
            emptyText={
              showMappingWarning
                ? 'Configure Autotask mapping to view tickets'
                : selectedTenant
                  ? 'No tickets found'
                  : 'Select a company to view tickets'
            }
          >
            <StyledTable
              dataSource={tickets || []}
              columns={ticketColumns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true }}
              size="small"
            />
          </DataCard>
        </Col>
      </Row>
    </DashboardLayout>
  );
};

export default ServiceDeskPage;
